import {
  FC,
  KeyboardEvent,
  MouseEvent,
  PointerEvent,
  useEffect,
  useRef,
  useState,
} from 'react';
import { fabric } from 'fabric';
import styled from 'styled-components';
import {
  Calendar,
  Clock,
  Globe,
  Grid,
  Lock,
  Maximize,
  Square,
  Unlock,
} from 'react-feather';
import { useEditorData, useEditorHistory } from '../../lib/FabricJs';
import { useHandleHistory } from '../../lib/FabricJs/hooks/useHandleHistory';
import { setObjectLocked } from '../../lib/FabricJs/hooks/useHandleObjectLock';
import { editorTheme } from '../../theme';
import {
  getLayerName,
  getLayerPreview,
  isWidget,
  LayerPreview,
} from './layerInfo';

const REFRESH_EVENTS = [
  'object:added',
  'object:removed',
  'object:modified',
  'selection:created',
  'selection:updated',
  'selection:cleared',
  'text:changed',
];

const GroupLabel = styled.h3`
  margin: 12px 0 6px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${editorTheme.textMuted};
  &:first-child {
    margin-top: 0;
  }
`;

const List = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const Row = styled.li<{
  selected: boolean;
  dragging: boolean;
  dropBefore: boolean;
  dropAfter: boolean;
}>`
  position: relative;
  opacity: ${({ dragging }) => (dragging ? 0.5 : 1)};
  height: 40px;
  display: flex;
  align-items: center;
  gap: 4px;
  padding-right: 4px;
  border-radius: 6px;
  background: ${({ selected }) =>
    selected ? editorTheme.accentSoft : 'transparent'};
  &:hover {
    background: ${({ selected }) =>
      selected ? editorTheme.accentSoft : editorTheme.surfaceMuted};
  }
  &::before,
  &::after {
    content: '';
    position: absolute;
    left: 4px;
    right: 4px;
    height: 2px;
    background: ${editorTheme.accent};
    border-radius: 1px;
  }
  &::before {
    top: -2px;
    display: ${({ dropBefore }) => (dropBefore ? 'block' : 'none')};
  }
  &::after {
    bottom: -2px;
    display: ${({ dropAfter }) => (dropAfter ? 'block' : 'none')};
  }
`;

const Grip = styled.span<{ enabled: boolean }>`
  width: 20px;
  height: 100%;
  flex: none;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  letter-spacing: -2px;
  color: ${editorTheme.textMuted};
  opacity: 0.5;
  cursor: grab;
  touch-action: none;
  user-select: none;
  visibility: ${({ enabled }) => (enabled ? 'visible' : 'hidden')};
  &:hover {
    opacity: 1;
  }
`;

const SelectButton = styled.button`
  flex: 1;
  min-width: 0;
  height: 100%;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0;
  background: none;
  color: ${editorTheme.text};
  text-align: left;
  font-size: 13px;
`;

const Name = styled.span`
  flex: 1;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Thumb = styled.span`
  width: 36px;
  height: 26px;
  flex: none;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border: 1px solid ${editorTheme.border};
  border-radius: 4px;
  background: ${editorTheme.surfaceMuted};
  color: ${editorTheme.textMuted};
  font-size: 13px;
  & img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const Badge = styled.span`
  flex: none;
  font-size: 10px;
  font-family: monospace;
  color: ${editorTheme.accentHover};
  background: ${editorTheme.surface};
  border: 1px solid ${editorTheme.accentSoft};
  padding: 1px 5px;
  border-radius: 8px;
`;

const LockButton = styled.button<{ locked: boolean }>`
  width: 28px;
  height: 28px;
  flex: none;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  background: none;
  color: ${({ locked }) => (locked ? '#c2410c' : editorTheme.textMuted)};
  opacity: ${({ locked }) => (locked ? 1 : 0.55)};
  &:hover {
    opacity: 1;
    background: ${editorTheme.surface};
  }
`;

const RenameInput = styled.input`
  flex: 1;
  min-width: 0;
  height: 28px;
  font-size: 13px;
  padding: 0 6px;
  border: 1px solid ${editorTheme.accent};
  border-radius: 4px;
  outline: none;
`;

const Hint = styled.p`
  margin: 16px 0 0;
  font-size: 12px;
  line-height: 1.5;
  color: ${editorTheme.textMuted};
`;

const Preview: FC<{ preview: LayerPreview }> = ({ preview }) => {
  switch (preview.kind) {
    case 'image':
      return (
        <Thumb>
          <img src={preview.src} alt="" loading="lazy" />
        </Thumb>
      );
    case 'text':
      return (
        <Thumb style={{ fontFamily: preview.fontFamily, color: preview.color }}>
          Aa
        </Thumb>
      );
    case 'swatch':
      return (
        <Thumb>
          <span
            style={{
              width: 14,
              height: 14,
              background: preview.color,
              borderRadius: preview.round ? '50%' : 2,
              border: `1px solid ${editorTheme.border}`,
            }}
          />
        </Thumb>
      );
    case 'icon': {
      const icons = {
        clock: Clock,
        date: Calendar,
        web: Globe,
        group: Grid,
        shape: Square,
        qr: Maximize,
      };
      const Icon = icons[preview.icon];
      return (
        <Thumb>
          <Icon size={14} />
        </Thumb>
      );
    }
  }
};

const objectKeys = new WeakMap<fabric.Object, string>();
let nextKey = 0;
const getKey = (object: fabric.Object) => {
  // @ts-ignore: ids are assigned when history snapshots are taken.
  if (object.id) return object.id as string;
  if (!objectKeys.has(object)) objectKeys.set(object, `layer-${nextKey++}`);
  return objectKeys.get(object) as string;
};

const LayersPanel: FC = () => {
  const { canvas } = useEditorData();
  const { history } = useEditorHistory();
  const { handleHistory } = useHandleHistory();
  const [, setVersion] = useState(0);
  const [renaming, setRenaming] = useState<fabric.Object | null>(null);
  const [draft, setDraft] = useState('');
  const [dragged, setDragged] = useState<fabric.Object | null>(null);
  const [dropIndex, setDropIndex] = useState<number | null>(null);
  const layerListRef = useRef<HTMLUListElement>(null);

  useEffect(() => {
    if (!canvas) return;
    const refresh = () => setVersion((version) => version + 1);
    REFRESH_EVENTS.forEach((name) => canvas.on(name, refresh));
    return () => REFRESH_EVENTS.forEach((name) => canvas.off(name, refresh));
  }, [canvas]);
  useEffect(() => {
    const frame = requestAnimationFrame(() =>
      setVersion((version) => version + 1),
    );
    return () => cancelAnimationFrame(frame);
  }, [history.present]);

  if (!canvas) return null;

  const objects = canvas.getObjects();
  // Front first, like the stacking on the slide.
  const widgets = objects.filter(isWidget).reverse();
  const layers = objects.filter((object) => !isWidget(object)).reverse();
  const selected = canvas.getActiveObjects();

  const select = (object: fabric.Object, event: MouseEvent) => {
    const additive = event.shiftKey || event.metaKey || event.ctrlKey;
    let next = [object];
    if (additive) {
      next = selected.includes(object)
        ? selected.filter((item) => item !== object)
        : [...selected, object];
    }
    canvas.discardActiveObject();
    if (next.length === 1) {
      canvas.setActiveObject(next[0]);
    } else if (next.length > 1) {
      canvas.setActiveObject(new fabric.ActiveSelection(next, { canvas }));
    }
    canvas.requestRenderAll();
  };

  const toggleLock = (object: fabric.Object) => {
    setObjectLocked(object, !object.lockMovementX);
    canvas.requestRenderAll();
    handleHistory(canvas);
  };

  const startRename = (object: fabric.Object) => {
    setRenaming(object);
    setDraft(getLayerName(object));
  };

  const finishRename = (commit: boolean) => {
    const object = renaming;
    setRenaming(null);
    if (!commit || !object) return;
    const name = draft.trim();
    const current = object.data?.name || '';
    if (name === current || (!current && name === getLayerName(object))) {
      return;
    }
    const data = { ...(object.data || {}) };
    if (name) data.name = name;
    else delete data.name;
    object.set('data', data);
    handleHistory(canvas);
  };

  // Moves `object` so it sits at `index` in the front-first list, keeping
  // widgets where they are.
  const moveLayer = (object: fabric.Object, index: number) => {
    const from = layers.indexOf(object);
    if (from === -1) return;
    const target = from < index ? index - 1 : index;
    if (target === from) return;
    const reordered = layers.filter((layer) => layer !== object);
    reordered.splice(target, 0, object);
    const backToFront = reordered.reverse();
    const next = [...objects];
    let slot = 0;
    objects.forEach((item, position) => {
      if (!isWidget(item)) next[position] = backToFront[slot++];
    });
    // @ts-ignore: Fabric has no public API to reorder several objects.
    canvas._objects.splice(0, next.length, ...next);
    canvas.requestRenderAll();
    handleHistory(canvas);
  };

  // Pointer events (not HTML drag and drop) so reordering also works with
  // touch and pens; the grip captures the pointer for the whole drag.
  const getDropIndex = (clientY: number) => {
    const rows = Array.from(layerListRef.current?.children || []);
    const index = rows.findIndex((row) => {
      const { top, height } = row.getBoundingClientRect();
      return clientY < top + height / 2;
    });
    return index === -1 ? rows.length : index;
  };

  const startDrag = (event: PointerEvent, object: fabric.Object) => {
    if (event.button !== 0) return;
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    setDragged(object);
    setDropIndex(getDropIndex(event.clientY));
  };

  const moveDrag = (event: PointerEvent) => {
    if (dragged) setDropIndex(getDropIndex(event.clientY));
  };

  const endDrag = (drop: boolean) => {
    if (drop && dragged && dropIndex !== null) moveLayer(dragged, dropIndex);
    setDragged(null);
    setDropIndex(null);
  };

  const renderRow = (
    object: fabric.Object,
    index: number,
    reorderable: boolean,
  ) => {
    const locked = !!object.lockMovementX;
    const name = getLayerName(object);
    const isRenaming = renaming === object;
    const kvsKey = object.data?.type === 'kvs' ? object.data.kvsKey : null;
    return (
      <Row
        key={getKey(object)}
        selected={selected.includes(object)}
        dragging={dragged === object}
        dropBefore={reorderable && !!dragged && dropIndex === index}
        dropAfter={
          reorderable &&
          !!dragged &&
          dropIndex === index + 1 &&
          index === layers.length - 1
        }
      >
        <Grip
          enabled={reorderable && !isRenaming}
          aria-hidden="true"
          title="Drag to change the order"
          onPointerDown={(event) => startDrag(event, object)}
          onPointerMove={moveDrag}
          onPointerUp={() => endDrag(true)}
          onPointerCancel={() => endDrag(false)}
        >
          ⋮⋮
        </Grip>
        {isRenaming ? (
          <RenameInput
            autoFocus
            aria-label="Layer name"
            value={draft}
            onFocus={(event) => event.target.select()}
            onChange={(event) => setDraft(event.target.value)}
            onBlur={() => finishRename(true)}
            onKeyDown={(event: KeyboardEvent<HTMLInputElement>) => {
              if (event.key === 'Enter') finishRename(true);
              if (event.key === 'Escape') finishRename(false);
            }}
          />
        ) : (
          <SelectButton
            title="Click to select, Shift-click to add, double-click to rename"
            aria-pressed={selected.includes(object)}
            onClick={(event) => select(object, event)}
            onDoubleClick={() => startRename(object)}
          >
            <Preview preview={getLayerPreview(object)} />
            <Name>{name}</Name>
            {kvsKey && <Badge title={`KVS key ${kvsKey}`}>KVS</Badge>}
          </SelectButton>
        )}
        <LockButton
          locked={locked}
          aria-label={locked ? `Unlock ${name}` : `Lock ${name}`}
          title={locked ? 'Unlock' : 'Lock'}
          onClick={() => toggleLock(object)}
        >
          {locked ? <Lock size={14} /> : <Unlock size={14} />}
        </LockButton>
      </Row>
    );
  };

  if (!objects.length) {
    return <Hint>No objects on this slide yet.</Hint>;
  }

  return (
    <>
      {widgets.length > 0 && (
        <>
          <GroupLabel>Apps · always in front</GroupLabel>
          <List aria-label="Apps">
            {widgets.map((object, index) => renderRow(object, index, false))}
          </List>
        </>
      )}
      <GroupLabel>Layers · front to back</GroupLabel>
      <List aria-label="Layers" ref={layerListRef}>
        {layers.map((object, index) => renderRow(object, index, true))}
      </List>
      <Hint>
        Drag to change the order. Shift-click to select several. Double-click a
        name to rename it.
      </Hint>
    </>
  );
};

export default LayersPanel;
